import {type User, UserRole} from '~/types/account'
import {useRootStore} from '~/stores/root'
import {validSessionExpiry} from '~/utils/persistent-query'
import {cartMerge, cartReplace, initCart, initCartSummary} from "~/utils/cart"
import {useLocaleStore} from "~/stores/locale"
import {useCartStore} from "~/stores/cart"
import {aiData, getAuthKeyFromCookie, getIsRecruitFromCookie} from "~/utils/auth"
import {assertIsDefined} from "~/utils/assert"
import {usePersistentStore} from "~/stores/persistent"
import {useLocalizedTo} from "~/composables/locale"
import {Language, type Culture} from "~/types/locale"
import type {DemoAuthResponse, SingInResponse} from "~/types/auth"
import type {RouterMethod} from "h3"
import {parseQuery} from "vue-router"
import {useTypedRuntimeConfig} from '~/utils/runtimeConfig'
import {stripSubLocalePrefix} from '~/utils/locale'
import { JOIN_ROOT } from '~/utils/joinRoutes'
import { isServer } from '~/utils/env'
// Session-scoped state (the bonus selection, AB#63454) is cleared through listeners, not imported here; see sessionChange.ts.
import { announceSessionChange } from '~/utils/sessionChange'
import type { Ref } from 'vue'
import type { NuxtApp } from '#app'

// Options for login() and the auth store's authenticate(); one shape so a new flag cannot
// drift between them. AB#64387
export interface LoginOptions {
  /** Stay on the current route: no post-login navigation (e.g. the join existing-customer step). */
  skipRedirects?: boolean
  /** Do not fetch the customer wishlist after sign-in (the join flow has no use for it). */
  skipWishlist?: boolean
}

interface AuthCredentials {
  username: string
  password: string

  [key: string]: unknown
}

const getAnonymousUser = (culture: Culture): User => ({
  firstName: '',
  lastName: '',
  email: '',
  role: UserRole.Anonymous,
  phoneNumber: '',
  loyaltyOptIn: false,
  errors: [],
  isBetaUser: false,
  isoBirthday: '',
  birthday: '',
  preferredContact: '',
  preferredLanguage: Language.EN,
  receiveCommunications: false,
  culture,
  defaultCulture: culture,
  actionsNeeded: {
    needsDemoDirectorySla: false,
    needsDemoLocatorSla: false,
    needsDirectDepositAccount: false,
    needsIdaAcceptance: false,
    needsGstIDConfirmation: false,
    needsGstNumberConfirmation: false,
    needsTeamLeaderSelection: false,
  },
})

export const useRedirect = () => {
  const route = useRoute()
  const router = useRouter()

  const rootStore = useRootStore()

  const redirect = (routeNew: string, replaceRoute: boolean = false, saveQueryParams: boolean = false) => {
    // Vue Router 4 reads only the path part of a `{ path }` location and drops a
    // query or hash written into it (vue-router 3, which nuxt2 used, kept them), so
    // split them off. The router's own parseQuery keeps a repeated key as a list, as
    // it would from a URL. Absolute URLs go to window.location as-is below.
    const isAbsolute = routeNew.startsWith('http')
    const hashStart = isAbsolute ? -1 : routeNew.indexOf('#')
    const hash = hashStart < 0 ? undefined : routeNew.slice(hashStart)
    const beforeHash = hashStart < 0 ? routeNew : routeNew.slice(0, hashStart)
    const queryStart = isAbsolute ? -1 : beforeHash.indexOf('?')
    const path = queryStart < 0 ? beforeHash : beforeHash.slice(0, queryStart)
    const pathQuery = queryStart < 0 ? undefined : parseQuery(beforeHash.slice(queryStart + 1))
    // Only the params the current route actually carries: an undefined one spread
    // over the path's own query erased it, so the path's query goes last.
    const savedQuery = !saveQueryParams ? {} : Object.fromEntries(Object.entries({
      unsubscribeEmail: route.query?.unsubscribeEmail,
      unsubscribeItemId: route.query?.unsubscribeItemId,
      subscribeEmail: route.query?.subscribeEmail,
      subscribeItemId: route.query?.subscribeItemId,
      // A demonstrator opening an invite link lands on /?invitation=<id>,
      // and the home page sends them to /portal through here before it
      // renders. Dropping the id strands the invite (nuxt2 keeps it too:
      // composables/auth.ts).
      invitation: route.query?.invitation
    }).filter(([, value]) => value !== undefined))
    const query = { ...savedQuery, ...pathQuery }
    const routeWithParams = {
      path,
      ...(Object.keys(query).length ? {query} : {}),
      ...(hash ? {hash} : {})
    }
    const {localizedTo} = useLocalizedTo(routeWithParams)
    const targetPath = typeof localizedTo.value === 'string' ? localizedTo.value : localizedTo.value?.path
    // '/fr' and '/fr/' are the same page: the localized home target is '/fr/', and
    // the header logo links to '/fr'. Without this the home page redirected to itself.
    const withoutTrailingSlash = (path?: string) => path?.replace(/\/+$/, '')
    // A query or hash the caller wrote into the path is part of where it is going:
    // redirect('/p?mode=x') from a bare /p still navigates. Saved params alone never
    // made the current page count as elsewhere, and still do not.
    const queryKey = (value: object = {}) => JSON.stringify(Object.entries(value).toSorted(([a], [b]) => a.localeCompare(b)))
    const samePage = withoutTrailingSlash(targetPath) === withoutTrailingSlash(route.path) &&
      (pathQuery === undefined || queryKey(query) === queryKey(route.query)) &&
      (hash === undefined || hash === route.hash)

    if (!samePage) {
      if (isAbsolute) {
        // Absolute URLs (cross-domain culture/region switches) are never
        // locale-prefixed, so use the original string. localizedTo wraps the
        // target in an object, which would stringify to "[object Object]" if
        // assigned to window.location.href, and which navigateTo rejects on the
        // server unless the target is declared external.
        if (isServer) {
          return navigateTo(routeNew, { external: true })
        }
        window.location.href = routeNew
        return true
      }
      if (isServer) {
        return navigateTo(localizedTo.value)
      }
      return replaceRoute ? router.replace(localizedTo.value!) : router.push(localizedTo.value!)
    }
  }
  // Navigate by full document load rather than by the router, keeping the
  // sub-locale prefix that `redirect` would have applied. The destination is an
  // ordinary internal path — deliberately not named "external", which in this
  // codebase means an off-site absolute URL (handleCategoryClick,
  // CmsContent's http/https new-tab rule). What is unusual here is the
  // mechanism, not the destination.
  //
  // Use this when the destination needs the SSR pass to run again, not merely a
  // different route. The join flow is the case that forced it: a recruit
  // request skips both `rootStore.fetchCategories()` and
  // `fetchAnonymousSession()` in init-auth.server.ts (AB#63360), and that plugin
  // is server-only. The join exits call joinStore.clearSession() first, which
  // re-fetches both client-side through logout(false) — but clearSession
  // swallows logout's errors, so when either fetch rejects this reload is the
  // only thing that repopulates them. It is also the single navigation: logout
  // is told not to push home itself. Whether the hybrid guard would force a
  // reload instead depends on '/' being in hybridRoutes, which differs per
  // environment (dev lists it, QA and prod do not yet) — not a decision this
  // flow can delegate.
  const redirectWithReload = (routeNew: string) => {
    const {localizedTo} = useLocalizedTo(routeNew)
    const target = typeof localizedTo.value === 'string'
      ? localizedTo.value
      : (localizedTo.value?.path ?? routeNew)

    // A server-side redirect is already a full document load from the
    // browser's point of view, so this is the same intent rather than a
    // fallback — and it keeps `window` out of the SSR path, as redirect() does.
    if (import.meta.server) {
      return navigateTo(target)
    }
    window.location.href = target
  }

  const {isDemo, isAgent} = useSAuthState()

  // Whose home is the portal: redirectHome sends them there.
  const homeIsPortal = () => isDemo.value || (isAgent.value && rootStore.isActiveDemo)

  const redirectHome = (saveQueryParams: boolean = false) => {
    if (homeIsPortal()) {
      return redirect('/portal', false, saveQueryParams)
    } else {
      return redirect('/', false, saveQueryParams)
    }
  }

  return {redirect, redirectWithReload, redirectHome, homeIsPortal}
}

// The session JWT from the httpOnly auth_cookie. It is held per NuxtApp (one per SSR
// request) rather than in useState, because every useState value is serialized into
// window.__NUXT__ and would put the token in page source for any script to read. nuxt2
// kept it out of Vuex state for the same reason. Only the server reads it, to forward the
// cookie on SSR API calls (withRequestCookies); the browser sends the cookie itself.
const sessionTokens = new WeakMap<NuxtApp, Ref<string | undefined>>()
const useSessionToken = () => {
  const nuxtApp = useNuxtApp()
  let token = sessionTokens.get(nuxtApp)
  if (!token) {
    token = shallowRef<string | undefined>()
    sessionTokens.set(nuxtApp, token)
  }
  return token
}

export const useSAuthState = () => {
  const persistentStore = usePersistentStore()
  const localeStore = useLocaleStore()
  const data = useState<User>("auth:data", () => getAnonymousUser(localeStore.culture));

  const loading = useState("auth:loading", () => false);
  const loggedIn = useState("auth:loggedIn", () => false);
  const token = useSessionToken();
  const status = computed(() => {
    if (loading.value) {
      return "loading";
    }
    if (loggedIn.value) {
      return "authenticated";
    }
    return "unauthenticated";
  });
  const isAuthenticated = computed(() => status.value === 'authenticated' && !!data.value && ![UserRole.Anonymous, UserRole.Agent].includes(data.value.role))
  const user = computed(() => data.value)
  const userRole = computed(() => user.value.role)
  const isGuest = computed(() => userRole.value === UserRole.Guest)
  const isDemo = computed(() => {
    try {
      return userRole.value === UserRole.Demonstrator ||
        (userRole.value === UserRole.Agent &&
          !!persistentStore.getPersistentValue('isDemo'))
    } catch {
      return false
    }
  })
  const isCustomerOrDemo = computed(() => userRole.value === UserRole.Customer || isDemo.value)
  const isCustomer = computed(() => userRole.value === UserRole.Customer)
  const isAgent = computed(() => userRole.value === UserRole.Agent)
  const isRecruit = computed(() => userRole.value === UserRole.Recruit)
  const isAgentOrImpersonating = computed(() => isAgent.value || persistentStore.impersonationContext.isImpersonating)
  // An agent walking the join as a test recruit (join/agent-recruit): the session is a
  // recruit's, with the agent riding on it, so it can reach the join checkout and no
  // payment method can be added. Submit is still shown and sent -- the backend refuses it
  // and the message is surfaced; there is no client-side submit gate. AB#64477
  const isAgentRecruit = computed(() =>
    persistentStore.impersonationContext.isImpersonating
    && persistentStore.impersonationContext.impersonatedRole === UserRole.Recruit)


  return {
    data,
    isAuthenticated,
    isAgentOrImpersonating,
    isAgentRecruit,
    isAgent,
    isGuest,
    isDemo,
    isRecruit,
    isCustomerOrDemo,
    isCustomer,
    loggedIn,
    user,
    userRole,
    token,
    loading,
    status
  };
}
/**
 * Hand a signing-in demonstrator's identity over to /help/account-linking.
 *
 * nuxt2 pushed all three values as route params. vue-router 4 discards params
 * the target route's path does not declare, so `/help/account-linking` received
 * none of them: step 1 rendered an empty name and SignInLinking posted
 * `demoToken: undefined` to account/link-demo, which could never link. The
 * payload is stashed in the account store for the one hop instead of moving to
 * a query string, because a live demo token does not belong in the URL.
 */
export const startDemoAccountLinking = async (demo: DemoAuthResponse) => {
  const accountStore = useAccountStore()
  const router = useRouter()

  accountStore.setPendingDemoLink(demo)

  return await router.push({name: 'help-account-linking'})
}

// One session-expiry timer for the whole app, as nuxt2's auth plugin had. It
// used to live inside useSAuth(), so each caller (login, logout, the page-load
// plugin) armed its own and clearTimeout() only cleared that caller's: a timer
// armed on page load would survive a sign-in and fire at the old expiry. Client
// only (createTimeout is gated on import.meta.client), so it is never shared
// across server requests. AB#64457
let timeoutId: number | null = null

// setTimeout runs a longer delay at once.
const MAX_TIMEOUT_MS = 2 ** 31 - 1

export const useSAuth = () => {
  const {isAuthenticated, data, loggedIn, token, loading} = useSAuthState()
  const rootStore = useRootStore()
  const localeStore = useLocaleStore()
  const cartStore = useCartStore()
  const persistentStore = usePersistentStore()
  const accountStore = useAccountStore()
  const config = useTypedRuntimeConfig()
  const reqCookie = useRequestHeaders(['cookie'])
  const nuxtApp = useNuxtApp()
  const {$api: api, $appInsights} = nuxtApp

  const router = useRouter()
  const route = useRoute()
  const {localizedTo: localizedHome} = useLocalizedTo('/')

  const clearTimeout = () => {
    if (timeoutId) {
      window.clearTimeout(timeoutId)
      timeoutId = null
    }
  }
  const triggerHandleTokenExpiration = async () => {
    if (isAuthenticated.value) {
      rootStore.updateTimeoutMenu(true)
    } else {
      await rootStore.fetchAnonymousSession()
      await cartReplace(true)
      createTimeout()
    }
  }

  // Defaults to the persisted expiry, which is what every re-arm after load wants.
  // An invalid expiry is ignored, leaving any armed timer in place.
  const createTimeout = (tokenExpDate: number | null = persistentStore.getPersistentValue('expirationDialogTime')) => {
    const expiry = validSessionExpiry(tokenExpDate)
    if (import.meta.client && expiry) {
      // show timeout dialog one minute before token expiry
      const tokenExp = expiry * 1000 - new Date().getTime() - 1000 * 60
      clearTimeout()
      if (tokenExp <= MAX_TIMEOUT_MS) {
        timeoutId = window.setTimeout(triggerHandleTokenExpiration, tokenExp)
      } else {
        // setTimeout overflows past ~24.8 days (it would fire at once), so wait
        // in chunks and re-arm until the delay fits.
        // If the expiry passed meanwhile (suspended machine), handle it now.
        timeoutId = window.setTimeout(() => {
          if (expiry * 1000 <= Date.now()) {
            timeoutId = null
            void triggerHandleTokenExpiration()
          } else {
            createTimeout(expiry)
          }
        }, MAX_TIMEOUT_MS)
      }
    }
  }

  const setAuthCookie = (tokenValue: string) => {
    // isServer rather than import.meta.client, so a spec can mock it (AB#64750)
    if (!isServer) {
      throw new Error('setAuthCookie should only be called on the server')
    }
    let expirationDialogTime = validSessionExpiry(persistentStore.getPersistentValue('expirationDialogTime'))

    if (!expirationDialogTime) {
      // one day from now
      expirationDialogTime = Math.floor(new Date().getTime() / 1000) + 60 * 60 * 24
      persistentStore.updateValue('expirationDialogTime', expirationDialogTime, new Date(expirationDialogTime * 1000))
    }

    const cookieExpDate = new Date(expirationDialogTime * 1000)
    const domain = getStampinupDomain(localeStore.culture)
    nuxtApp.runWithContext(() => {
      const cookie = useCookie<string>('auth_cookie', {
        secure: true,
        httpOnly: true,
        sameSite: 'none',
        domain: domain!,
        expires: cookieExpDate
      })
      cookie.value = tokenValue
    })
  }

  const logout = async (pushHome: boolean = true, leaveDomain = false) => {
    try {
      announceSessionChange(nuxtApp)
      // if we are in the join flow or a guest, we don't want to show the loading spinner since we logout and stay on the confirmation page
      assertIsDefined(data.value, 'user should be defined on logout')
      assertIsDefined(data.value.firstName, 'user should be defined on logout')
      if (![UserRole.Guest, UserRole.Recruit].includes(data.value.role)) {
        rootStore.rootLoading = true
      }

      persistentStore.updateValue('isDemo', false)
      persistentStore.updateValue('sharedCartId', null)
      // A demo who abandons the account-linking hand-off (or signs out from the
      // page itself) must not leave a live demo token sitting in the store for
      // the rest of the SPA session. The page's finish() clears it on the happy
      // path; this covers every other way out.
      accountStore.setPendingDemoLink(null)

      // Clear agent cookies and impersonation context on logout. Same exit path
      // as the agent store's clearImpersonation (app/stores/agent.ts): without
      // it, logging out of an impersonated session leaves isAgentLoggedIn /
      // isAgentImpersonated / agentEmail behind and the next anonymous session
      // is still read as an agent session. The role check alone is not enough —
      // while impersonating, the signed-in role is the impersonated user's.
      if (data.value.role === UserRole.Agent || persistentStore.impersonationContext.isImpersonating) {
        persistentStore.clearImpersonationContext()
        // equivalent to agentStore.updateViewAsDate(null), written against the
        // persistent store directly so useSAuth need not instantiate the agent
        // store, whose own setup resolves useSAuth.
        persistentStore.updateValue('viewAsDate', null)
        useCookie('agentEmail', { secure: true, httpOnly: false }).value = null
        useCookie('isAgentLoggedIn', { secure: true, httpOnly: false }).value = null
        useCookie('isAgentImpersonated', { secure: true, httpOnly: false }).value = null
      }

      // getting an anonymous session will essentially log us out
      await rootStore.fetchAnonymousSession()

      // if we are logged out and leaving the domain then there is no need to do anything else just get out
      if (leaveDomain) {
        return
      }
      await rootStore.fetchCategories()

      createTimeout()
      cartStore.cart = initCart()
      cartStore.summary = initCartSummary()
      rootStore.updateTimeoutMenu(false)

      // NOTE: nuxt2 plugins/auth.ts:267 also gates this on
      // `$auth.user?.role !== UserRole.Guest`, intending that a guest logging out
      // from the order confirmation page stays on it. That guard is dead in BOTH
      // repos and was deliberately not ported: fetchAnonymousSession() above calls
      // fetchSession(), which overwrites `data.value` with the anonymous session
      // (auth.ts fetchSession -> `data.value = response`), so by the time this line
      // runs the role is Anonymous and never Guest. Making it work means capturing
      // the role before fetchAnonymousSession, which would change what production
      // does today — a product decision, raised on the parity feature (AB#63250).
      if (pushHome && import.meta.client) {
        window.location.replace(typeof localizedHome.value === 'string' ? localizedHome.value : localizedHome.value?.path || '/')
        return
      } else {
        rootStore.rootLoading = false
      }
    } catch (error) {
      console.error('Logout failed:', error)
      rootStore.rootLoading = false
      throw error
    }
  }
  const fetchSession = async () => {
    const {path, method} = config.public.auth.provider.endpoints.getSession;
    if (import.meta.server && !token.value) {
      // initial first hit, when there no cookies
      if (!reqCookie.cookie) {
        return
      }
      token.value = getAuthKeyFromCookie(reqCookie.cookie)
    }
    const headers = isServer && token.value ? new Headers({'Cookie': `auth_cookie=${token.value}`}) : reqCookie
    loading.value = true;
    try {
      const response = await api<User>(path, {method: method as RouterMethod, headers});
      data.value = response;
      loggedIn.value = true
    } catch (_err) {
      data.value = getAnonymousUser(localeStore.culture)
      loggedIn.value = false
      const isRecruitJoinSsrRequest = import.meta.server
        && getIsRecruitFromCookie(reqCookie.cookie ?? '') === 'true'
        && stripSubLocalePrefix(route.path, route.params.lang as string | undefined).startsWith(JOIN_ROOT)
      if (!isRecruitJoinSsrRequest) {
        token.value = undefined
      }
    }
    loading.value = false;
    return data.value;
  }
  const signIn = async (credentials: AuthCredentials) => {
    const {path, method} = config.public.auth.provider.endpoints.signIn

    const response = await api<SingInResponse>(path, {
      method: method as RouterMethod,
      body: credentials
    })

    await nextTick(fetchSession)

    return response

  }
  const handlePostLoginRedirects = async (response: SingInResponse) => {
    const demoStore = useDemoStore()
    if (data.value.role === UserRole.Demonstrator) {
      // The sign-in has already succeeded; a failed contact fetch must not reject
      // login(), so skip the demo locale redirect and carry on signed in. AB#64648
      try {
        const contact = await demoStore.fetchContact()
        const newUrl = localeStore.getDemoLocaleUrl(contact)
        // Returns true so login() stops, as for the culture switch below: the
        // page is leaving for the demonstrator's own domain. The sign-in is
        // counted first: userAuthentication is sent only from login(), so the
        // destination page never sends it and the sign-in would be lost (nuxt2
        // dropped it here too). Clarity and App Insights are not repeated, as
        // auth-session.client sets both on every signed-in page load.
        if (newUrl) {
          const {proxy: gtm} = useScriptGoogleTagManager()
          pushUserAuthentication(gtm, data.value)
          window.location.replace(newUrl)
          return true
        }
      } catch (error) {
        console.error('Post-login demo contact fetch failed:', error)
      }
    }
    // Sign the user out and send them to their own domain to sign in there.
    // Carrying the session across (changeAuthenticatedLocale's authToken
    // hand-off) is what nuxt2 PR 19360 removed after reports of users being
    // "signed in as someone else" (nuxt2 plugins/auth.ts:146-155). Returns true
    // so login() stops: the page is leaving, and the cart merge and wishlist
    // fetch would run against the anonymous session logout just minted. AB#64448
    if (data.value.defaultCulture && localeStore.culture !== data.value.defaultCulture) {
      const defaultCulture = data.value.defaultCulture
      await logout(false, true)
      window.location.replace(localeStore.getBaseUrl(defaultCulture))
      return true
    }

    // used both by logging in with demoId and account linking.  (Need to continue if not linking)
    if (route.path.includes('account-linking')) {
      return response
    }
    if (response.demoToken && !router.currentRoute.value.path.includes('account-linking')) {
      return startDemoAccountLinking({
        demoToken: response.demoToken,
        firstName: response.firstName,
        lastName: response.lastName
      })
    }
  }
  // The sign-in has already succeeded by the time this runs, so nothing in it may
  // reject login(): a failed cart merge or wishlist fetch would otherwise reach the
  // sign-in form's catch, which skips emits('authenticated') and leaves a signed-in
  // user stranded on the form. Each fetch is settled and logged on its own. AB#64648
  const handlePostLoginDataSync = async (sharedCartId: string | null, options?: Pick<LoginOptions, 'skipWishlist'>) => {

    // replace cart if checkout was clicked. On desktop will still be on cart page, but mobile and tablet will be sent to auth page, so we need checkoutClicked in store
    try {
      if (sharedCartId) {
        await cartStore.replaceCart()
      } else if (rootStore.checkoutClicked) {
        await cartReplace()
      } else {
        await cartMerge()
      }
    } catch (error) {
      console.error('Post-login cart sync failed:', error)
    }

    const wishlistStore = useWishlistStore()
    // No cartStore.fetchCart() here. It was added while cartMerge/cartReplace
    // were stubbed out; now that they are live it is redundant on every branch
    // that acts, and it actively defeats their guards — it would re-fetch during
    // a culture redirect, and over a demo cart that cartMerge deliberately left
    // alone. Nuxt2 plugins/auth.ts has no such fetch.
    // The join flow signs in here only to become a recruit and has no use for the customer
    // wishlist (4.5 s on dev in a recorded join), so it skips it. AB#64387
    const results = await Promise.allSettled([
      options?.skipWishlist ? Promise.resolve() : wishlistStore.fetchAllItems(),
      rootStore.fetchCategories()
    ])
    for (const result of results) {
      if (result.status === 'rejected') {
        console.error('Post-login fetch failed:', result.reason)
      }
    }

    if (data.value?.loyaltyOptIn) {
      accountStore.fetchLoyaltyInventory()
    }

  }
  const handlePostLoginExpiration = (response: SingInResponse) => {
    rootStore.updateTimeoutMenu(false)
    const cookieExpiration = new Date(response.cookieExpiration * 1000)
    persistentStore.updateValue('expirationDialogTime', response.cookieExpiration, cookieExpiration)
    // Role cookie is now managed by the API via Set-Cookie header


    const isDemoCookie = useCookie('isDemo')
    persistentStore.updateValue('isDemo', !!isDemoCookie.value)
    createTimeout(response.cookieExpiration)
  }
  const handlePostLoginAnalytics = () => {
    // added for integration with userpilot (pushing user to datalayer)
    const {proxy: gtm} = useScriptGoogleTagManager()
    pushUserAuthentication(gtm, data.value)
    //
    // update clarity custom vars
    identify(getCookieValue('ai_user') as string, aiData(data.value.email))
    $appInsights.setAuthenticatedUserContext(data.value.email)
  }
  const handleFinalRedirects = () => {
    if (rootStore.redirectRoute) {
      const redirectRoute = rootStore.redirectRoute
      rootStore.redirectRoute = ''
      return router.push(redirectRoute)
    }

    if (data.value?.role === UserRole.Demonstrator) {
      if (rootStore.demoLoginRedirect) {
        // An invitation link drops the demo on the sign-in page carrying
        // ?invitation=; dropping it on the way to /portal strands the pending
        // invite. Matches Nuxt2 plugins/auth.ts:213-219.
        const invitation = route.query?.invitation
        return navigateTo(invitation ? { path: '/portal', query: { invitation } } : '/portal')
      }
      rootStore.demoLoginRedirect = true
    }

    // need to check route because mobile and tablet are sent to auth page and use redirectOnSuccess, so don't try to push them from here
    if (rootStore.checkoutClicked && cartStore.readyForCheckout && router.currentRoute.value.path.includes('cart')) {
      rootStore.checkoutClicked = false
      // With a bonus campaign active this returns to the cart, so the bonus step runs against the replaced cart (AB#63452).
      router.push(cartStore.checkoutAfterSignInPath)
    }
  }

  const login = async (credentials: AuthCredentials, options?: LoginOptions) => {
    const sharedCartId = persistentStore.getPersistentValue('sharedCartId')
    const params: {
      sharedCartId?: string
      username: string
      password: string
    } = credentials
    if (sharedCartId) {
      params.sharedCartId = sharedCartId
    }

    const previousUser = data.value

    let response: SingInResponse
    try {
      response = await signIn(params)
      // The API returned new cookies; pull them into the persistent store before
      // any redirect can short-circuit the flow. handlePostLoginExpiration also
      // syncs isDemo inline, but it runs after handlePostLoginRedirects, which
      // early-returns on the demo-locale, culture-switch, account-linking and
      // demoToken paths — so on those isDemo was never synced at all.
      persistentStore.updateStateFromServerCookies()
      // An agent's "view as" date must not leak into the next session.
      persistentStore.updateValue('viewAsDate', null)
    } catch (error) {
      // Leave the app on the user it had rather than half-authenticated.
      setUser(previousUser)
      throw error
    }

    announceSessionChange(nuxtApp)

    // Some flows (e.g. the join existing-customer step) authenticate only to
    // verify identity and must stay on the current route. Skip post-login
    // navigation so we don't culture-switch / redirect them off the flow.
    if (!options?.skipRedirects) {
      const redirected = await handlePostLoginRedirects(response);
      if (redirected) return;
    }

    await handlePostLoginDataSync(sharedCartId, { skipWishlist: options?.skipWishlist });
    handlePostLoginAnalytics();
    await handlePostLoginExpiration(response);

    if (!options?.skipRedirects) {
      await handleFinalRedirects();
    }
  }


  const setUser = (user: User) => {
    data.value = user
  }
  // Server only: the token is read by withRequestCookies on SSR calls, and the browser
  // sends the httpOnly cookie itself, so holding the JWT in browser memory serves nothing.
  const setUserToken = (tokenNew: string) => {
    if (isServer) {
      token.value = tokenNew
    }
  }

  const setUserRole = (role: UserRole) => {
    data.value.role = role
  }

  return {fetchSession, setAuthCookie, setUserRole, logout, login, setUser, setUserToken, createTimeout, ...useSAuthState()}
}
